import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { getCampaignDetails } from "@/sideEffects/api";
import { useQuery } from "@tanstack/react-query";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { ROUTES } from "@/constants/routeConstants";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { CampaignScheduleForm } from "./CampaignScheduleForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BaseFormLayout } from "@/pages/shared/BaseFormLayout";
import { DeleteCampaignButton } from "./DeleteCampaignButton";
import { CampaignUpdateMetaForm } from "./CampaignUpdateMetaForm";
import { CampaignTags } from "./CampaignTags";
import { CampaignPublishButton } from "./CampaignPublishButton";
import { CampaignDetailAssignedPlayers } from "./CampaignDetailAssignedPlayers";
import { CampaignScheduleDetail } from "./CampaignScheduleDetail";
import { CampaignType } from "@/lib/types";
import { CampaignRobotScheduleForm } from "./CampaignRobotScheduleForm";

export const CampaignDetailPage = () => {
  const { t } = useTranslation();
  const [queryParams] = useSearchParams();
  const campaignId = queryParams.get("Id");
  const activeFirm = useSelector(selectActiveFirm);

  const { data: campaignDetails, isLoading } = useQuery({
    queryFn: () => getCampaignDetails(String(campaignId), String(activeFirm?.firmGuid)),
    queryKey: ["getCampaignDetails", campaignId],
  });

  const campaignDetailsData = campaignDetails?.data;

  useBreadcrumbs([
    {
      name: t("entities.campaign"),
      path: ROUTES.CAMPAIGN,
    },
    {
      name: campaignDetailsData ? `${t("common.entityDetail", { name: campaignDetailsData?.name })}` : "...",
      path: ROUTES.CAMPAIGN_DETAIL,
    },
  ]);

  if (!campaignDetailsData || isLoading) {
    return <LoadingSmall />;
  }

  const isRobotType = campaignDetailsData.campaignType === CampaignType.Robot;

  return (
    <BaseFormLayout
      title={
        <>
          <div className="mb-2">{t("common.entityDetail", { name: campaignDetailsData.name })}</div>
          <CampaignTags campaignGuid={campaignDetailsData.id} campaignIntId={campaignDetailsData.intId} />
        </>
      }
      rightContent={
        <div className="flex gap-2">
          <CampaignPublishButton campaignGuid={campaignDetailsData.id} />
          <DeleteCampaignButton campaignId={campaignDetailsData.id} />
        </div>
      }
    >
      {isRobotType ? (
        <Tabs defaultValue="config">
          <TabsList>
            <TabsTrigger value="config">{t("common.config")}</TabsTrigger>
            <TabsTrigger value="schedule">{t("common.schedule")}</TabsTrigger>
            <TabsTrigger value="assignedPlayers">{t("common.assignedPlayers")}</TabsTrigger>
          </TabsList>
          <TabsContent value="config">
            <CampaignUpdateMetaForm campaignDetails={campaignDetailsData} />
          </TabsContent>
          <TabsContent value="schedule">
            <CampaignRobotScheduleForm campaignDetails={campaignDetailsData} />
          </TabsContent>
          <TabsContent value="assignedPlayers">
            <CampaignDetailAssignedPlayers campaignDetails={campaignDetailsData} />
          </TabsContent>
        </Tabs>
      ) : (
        <Tabs defaultValue="config">
          <TabsList>
            <TabsTrigger value="config">{t("common.config")}</TabsTrigger>
            <TabsTrigger value="schedule">{t("common.schedule")}</TabsTrigger>
            <TabsTrigger value="schedulePreview">{t("schedule.preview")}</TabsTrigger>
            <TabsTrigger value="assignedPlayers">{t("common.assignedPlayers")}</TabsTrigger>
          </TabsList>
          <TabsContent value="config">
            <CampaignUpdateMetaForm campaignDetails={campaignDetailsData} />
          </TabsContent>
          <TabsContent value="schedule">
            <CampaignScheduleForm campaignDetails={campaignDetailsData} />
          </TabsContent>
          <TabsContent value="schedulePreview">
            <CampaignScheduleDetail campaignDetails={campaignDetailsData} />
          </TabsContent>
          <TabsContent value="assignedPlayers">
            <CampaignDetailAssignedPlayers campaignDetails={campaignDetailsData} />
          </TabsContent>
        </Tabs>
      )}
    </BaseFormLayout>
  );
};
